
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getProductBySlug, Product } from '../api/products';
import { getImageUrl } from '../utils/image';
import { formatPrice } from '../utils/formatPrice';

const MarketingPopup: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const [isOpen, setIsOpen] = useState(false);
    const [product, setProduct] = useState<Product | null>(null);
    const [timeLeft, setTimeLeft] = useState(3600); // 1 hour in seconds

    useEffect(() => {
        // Show popup after 5 seconds
        const timer = setTimeout(() => {
            const hasSeenPopup = sessionStorage.getItem('hasSeenMarketingPopup');
            if (!hasSeenPopup) {
                setIsOpen(true);
                sessionStorage.setItem('hasSeenMarketingPopup', 'true');
            }
        }, 5000);

        // Fetch featured product
        const fetchProduct = async () => {
            try {
                const data = await getProductBySlug('huile-argan-bio');
                setProduct(data);
            } catch (error) {
                console.error('Failed to fetch featured product', error);
            }
        };

        fetchProduct();

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        const interval = setInterval(() => {
            setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => clearInterval(interval);
    }, [isOpen]);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s} `;
    };

    if (!product) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Popup Content */}
                    <motion.div
                        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col md:flex-row"
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    >
                        {/* Close Button */}
                        <button
                            onClick={() => setIsOpen(false)}
                            className={`absolute top - 4 ${isRTL ? 'left-4' : 'right-4'} z - 10 p - 2 bg - white / 80 rounded - full hover: bg - white transition - colors`}
                        >
                            <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Image Section */}
                        <div className="w-full md:w-1/2 relative min-h-[300px] md:min-h-full">
                            <img
                                src={getImageUrl(product.images[0])}
                                alt={isRTL ? product.title_ar : product.title_fr}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
                                <div className="text-white">
                                    <div className="bg-accent text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-3 animate-pulse">
                                        {t('popup.limitedOffer')}
                                    </div>
                                    <h3 className="text-3xl font-bold mb-2">{t('popup.specialDeal')}</h3>
                                    <p className="text-white/90">{t('popup.dontMiss')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Content Section */}
                        <div className="w-full md:w-1/2 p-8 flex flex-col justify-center bg-gradient-to-br from-white to-green-50">
                            <div className="mb-6">
                                <h2 className="text-2xl md:text-3xl font-bold text-dark mb-2">
                                    {isRTL ? product.title_ar : product.title_fr}
                                </h2>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="flex text-yellow-400">
                                        {[...Array(5)].map((_, i) => (
                                            <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                        ))}
                                    </div>
                                    <span className="text-sm text-gray-500">(128 reviews)</span>
                                </div>

                                <div className="flex items-end gap-3 mb-6">
                                    <span className="text-4xl font-bold text-primary">
                                        {formatPrice(product.discount_price || product.price, product.currency, isRTL ? 'ar-MA' : 'fr-MA')}
                                    </span>
                                    {product.discount_price && (
                                        <span className="text-xl text-gray-400 line-through mb-1">
                                            {formatPrice(product.price, product.currency, isRTL ? 'ar-MA' : 'fr-MA')}
                                        </span>
                                    )}
                                </div>

                                {/* Marketing Points */}
                                <div className="space-y-3 mb-8">
                                    <h4 className="font-bold text-dark">{t('popup.whyBuy')}</h4>
                                    <ul className="space-y-2">
                                        <li className="flex items-center gap-3 text-gray-700">
                                            <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            {t('popup.benefit1')}
                                        </li>
                                        <li className="flex items-center gap-3 text-gray-700">
                                            <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            {t('popup.benefit2')}
                                        </li>
                                        <li className="flex items-center gap-3 text-gray-700">
                                            <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            {t('popup.benefit3')}
                                        </li>
                                    </ul>
                                </div>

                                {/* Social Proof */}
                                <div className="flex items-center gap-2 mb-6 bg-orange-50 p-3 rounded-xl border border-orange-100">
                                    <div className="flex -space-x-2">
                                        <img className="w-8 h-8 rounded-full border-2 border-white" src="https://i.pravatar.cc/100?img=1" alt="" />
                                        <img className="w-8 h-8 rounded-full border-2 border-white" src="https://i.pravatar.cc/100?img=2" alt="" />
                                        <img className="w-8 h-8 rounded-full border-2 border-white" src="https://i.pravatar.cc/100?img=3" alt="" />
                                    </div>
                                    <p className="text-sm text-orange-800 font-medium">
                                        <span className="font-bold">24</span> {t('popup.peopleBought')}
                                    </p>
                                </div>

                                {/* Timer */}
                                <div className="flex items-center gap-2 mb-6 text-red-500 font-bold">
                                    <svg className="w-5 h-5 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {t('popup.hurry')} <span className="font-mono text-xl">{formatTime(timeLeft)}</span>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <Link
                                    to={`/ product / ${product.slug} `}
                                    className="block w-full py-4 bg-primary text-white text-center font-bold rounded-full hover:bg-primary-dark hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                                >
                                    {t('popup.getItNow')}
                                </Link>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="block w-full text-center text-sm text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    {t('popup.noThanks')}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default MarketingPopup;
